import { z } from 'zod';
import { SCHEDULE_KINDS, TARGET_TYPES } from '../constants.ts';
import { idSchema, unixMsSchema } from './common.ts';
import { manifestItemTypeSchema } from './manifest.ts';

export const targetTypeSchema = z.enum(TARGET_TYPES);
export const scheduleKindSchema = z.enum(SCHEDULE_KINDS);

// 'YYYY-MM-DD' in the screen's timezone.
const dateStringSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');
// 'HH:MM' 24h.
const timeStringSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'expected HH:MM');
// Bitmask: bit 0 = Mon … bit 6 = Sun. 127 = every day.
const daysMaskSchema = z.number().int().min(0).max(127);

export const scheduleSchema = z.object({
  id: idSchema,
  org_id: idSchema,
  playlist_id: idSchema,
  target_type: targetTypeSchema,
  target_id: idSchema.nullable(), // null for target_type=org
  start_date: dateStringSchema.nullable(),
  end_date: dateStringSchema.nullable(),
  days_mask: daysMaskSchema,
  start_time: timeStringSchema.nullable(),
  end_time: timeStringSchema.nullable(),
  priority: z.number().int(),
  enabled: z.boolean(),
  // Sprint 10 (docs/03 §Broadcast): 'regular' | 'broadcast'. Read-only on this response —
  // never settable through the schedules CRUD; broadcast rows are created only by
  // services/broadcast.ts. Defaults to 'regular', mirroring the DB column default
  // (migration 0010) — a resolved row built without an explicit kind (e.g. from
  // createScheduleSchema's output, which has no kind field at all) still validates.
  kind: scheduleKindSchema.default('regular'),
  // Sprint 10 (docs/02 §Layouts): per-schedule layout override. When set (and org-owned),
  // this schedule winning the resolver at manifest-build time swaps the screen's own
  // layout for this one, for as long as the schedule's window is active. Null = no
  // override (screens.layout_id, or fullscreen, applies as before).
  layout_id: idSchema.nullable(),
  created_at: unixMsSchema,
});
export type Schedule = z.infer<typeof scheduleSchema>;

export const createScheduleSchema = z
  .object({
    playlist_id: idSchema,
    target_type: targetTypeSchema,
    target_id: idSchema.nullable().default(null),
    start_date: dateStringSchema.nullable().default(null),
    end_date: dateStringSchema.nullable().default(null),
    days_mask: daysMaskSchema.default(127),
    start_time: timeStringSchema.nullable().default(null),
    end_time: timeStringSchema.nullable().default(null),
    priority: z.number().int().default(0),
    enabled: z.boolean().default(true),
    // No .default() (unlike the fields above): kept truly optional-in-output so the many
    // existing CreateSchedule object literals across the codebase don't all need a new
    // required field. The service normalises a missing value to null itself.
    layout_id: idSchema.nullable().optional(),
  })
  .refine((s) => s.target_type === 'org' || s.target_id !== null, {
    message: 'target_id is required unless target_type is "org"',
    path: ['target_id'],
  });
export type CreateSchedule = z.infer<typeof createScheduleSchema>;

// PATCH — all fields optional; validated without the org cross-field refine.
export const updateScheduleSchema = z.object({
  playlist_id: idSchema.optional(),
  target_type: targetTypeSchema.optional(),
  target_id: idSchema.nullable().optional(),
  start_date: dateStringSchema.nullable().optional(),
  end_date: dateStringSchema.nullable().optional(),
  days_mask: daysMaskSchema.optional(),
  start_time: timeStringSchema.nullable().optional(),
  end_time: timeStringSchema.nullable().optional(),
  priority: z.number().int().optional(),
  enabled: z.boolean().optional(),
  layout_id: idSchema.nullable().optional(),
});
export type UpdateSchedule = z.infer<typeof updateScheduleSchema>;

// GET /v1/schedules/preview?screen_id=&at= — resolver dry-run.
export const schedulePreviewQuerySchema = z.object({
  screen_id: idSchema,
  at: z.coerce.number().int().nonnegative().optional(), // unix ms; default = now
});
export type SchedulePreviewQuery = z.infer<typeof schedulePreviewQuerySchema>;

// Preview response: the winning playlist + its resolved items, plus every active
// candidate (with a `won` flag) so the form can warn about conflicts.
export const schedulePreviewCandidateSchema = z.object({
  schedule_id: idSchema,
  playlist_id: idSchema,
  playlist_name: z.string(),
  target_type: targetTypeSchema,
  priority: z.number().int(),
  won: z.boolean(),
});
export type SchedulePreviewCandidate = z.infer<typeof schedulePreviewCandidateSchema>;

export const schedulePreviewResultSchema = z.object({
  screen_id: idSchema,
  at: unixMsSchema,
  source: z.enum(['schedule', 'fallback', 'none']),
  playlist: z.object({ id: idSchema, name: z.string() }).nullable(),
  items: z.array(
    z.object({
      id: idSchema,
      // The resolver's own item type — reuses the manifest enum so this response can
      // never lag behind what playlistItemsToManifest actually emits (e.g. 'html').
      type: manifestItemTypeSchema,
      url: z.string(),
      duration_ms: z.number().int().positive(),
      fit: z.enum(['contain', 'cover', 'stretch']),
      // The media's human name (UX audit P1-5 — operators should read "Summer
      // hero", not engine jargon like "image · contain"). Optional: older API
      // deployments that predate this field simply omit it, and callers fall
      // back to type/fit.
      name: z.string().optional(),
    }),
  ),
  candidates: z.array(schedulePreviewCandidateSchema),
});
export type SchedulePreviewResult = z.infer<typeof schedulePreviewResultSchema>;
